import { ReactNode, CSSProperties } from 'react'
import { PortalKey } from '@/types'
import './PortalHero.css'

type PortalHeroKey = PortalKey | 'platform'

export interface PortalHeroProps {
  portal: PortalHeroKey
  eyebrow?: string
  title: string
  subtitle: string
  description?: string
  icon?: string
  logoSrc?: string
  highlights?: Array<{ label: string; value: string }>
  featureWords?: string[]
  actions?: ReactNode
}

/*
 * KAN-2035 — one Dhruvanta Retail identity across every portal hero.
 *
 * The old per-portal rainbow (teal seller / blue delivery / violet operations /
 * navy admin) read as five different products. They now share the retail lockup —
 * the deep-indigo rail ink #10243b grading into the warm commerce amber
 * (#c2410c / #9a3412, the `[data-product="retail"]` palette), lifted to #fdba74 for
 * the on-dark accent — and the EYEBROW is what distinguishes the role.
 *
 * These stay literal hexes (not tokens) on purpose: `buildLayeredBackground` parses
 * them with `hexToRgba` to derive the glow layers, which a `var(--…)` string cannot
 * satisfy. They are mirrored from the @dhruvanta/ui retail token block — if that
 * palette moves, update these five values with it.
 */
const RETAIL_HERO_ACCENT = '#fdba74'

const portalThemes: Record<
  PortalHeroKey,
  { background: string; accent: string; eyebrow: string }
> = {
  customer: {
    background: 'linear-gradient(135deg, #10243b 0%, #9a3412 100%)',
    accent: RETAIL_HERO_ACCENT,
    eyebrow: 'Customer Experience'
  },
  seller: {
    background: 'linear-gradient(135deg, #10243b 0%, #c2410c 100%)',
    accent: RETAIL_HERO_ACCENT,
    eyebrow: 'Seller Workspace'
  },
  delivery: {
    background: 'linear-gradient(135deg, #0b1a2b 0%, #9a3412 100%)',
    accent: RETAIL_HERO_ACCENT,
    eyebrow: 'Delivery Console'
  },
  operations: {
    background: 'linear-gradient(135deg, #10243b 0%, #7c2d12 100%)',
    accent: RETAIL_HERO_ACCENT,
    eyebrow: 'Operations Control Tower'
  },
  admin: {
    background: 'linear-gradient(135deg, #0b1a2b 0%, #7c2d12 100%)',
    accent: RETAIL_HERO_ACCENT,
    eyebrow: 'Executive Admin Center'
  },
  platform: {
    background: 'linear-gradient(135deg, #10243b 0%, #1b2f47 100%)',
    accent: RETAIL_HERO_ACCENT,
    eyebrow: 'Dhruvanta Retail Platform'
  }
}

export function PortalHero({
  portal,
  eyebrow,
  title,
  subtitle,
  description,
  icon,
  logoSrc,
  highlights = [],
  featureWords = [],
  actions
}: PortalHeroProps) {
  const theme = portalThemes[portal] ?? portalThemes.platform
  const layeredBackground = buildLayeredBackground(theme.background, theme.accent)
  // React's CSSProperties does not model CSS custom properties (--*), so type the style as
  // CSSProperties plus an index signature to carry the inline CSS variable.
  const heroStyle: CSSProperties & Record<`--${string}`, string> = {
    backgroundImage: layeredBackground,
    '--portal-hero-accent': theme.accent
  }
  const particleVariants = ['alpha', 'beta', 'gamma', 'delta', 'epsilon', 'zeta']

  return (
    <section
      className={`portal-hero portal-hero--${portal}`}
      style={heroStyle}
    >
      <div className="portal-hero__particles" aria-hidden="true">
        {particleVariants.map(variant => (
          <span key={variant} className={`portal-hero__particle portal-hero__particle--${variant}`} />
        ))}
      </div>
      <div className="portal-hero__content">
        <div className="portal-hero__pretitle">
          {logoSrc && (
            <img
              src={logoSrc}
              alt={`${title} mark`}
              className="portal-hero__logo"
              loading="lazy"
            />
          )}
          {icon && (
            <span className="portal-hero__icon" aria-hidden="true">
              {icon}
            </span>
          )}
          <span className="portal-hero__eyebrow">
            {eyebrow ?? theme.eyebrow}
          </span>
        </div>
        <h1 className="portal-hero__title">{title}</h1>
        <p className="portal-hero__subtitle">{subtitle}</p>
        {description && (
          <p className="portal-hero__description">{description}</p>
        )}
        {actions && <div className="portal-hero__actions">{actions}</div>}
        {!!highlights.length && (
          <dl className="portal-hero__highlights">
            {highlights.map(item => (
              <div key={item.label} className="portal-hero__highlight">
                <dt>{item.label}</dt>
                <dd>{item.value}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
      {featureWords.length > 0 && (
        <div className="portal-hero__features">
          {featureWords.map((word, index) => (
            <span key={`${word}-${index}`} className="portal-hero__feature-word">
              {word}
            </span>
          ))}
        </div>
      )}
    </section>
  )
}

const buildLayeredBackground = (baseGradient: string, accent: string): string => {
  const accentGlow = hexToRgba(accent, 0.3)
  const accentSecondary = hexToRgba(accent, 0.18)
  return [
    `radial-gradient(circle at 18% 25%, ${accentGlow}, transparent 55%)`,
    `radial-gradient(circle at 85% 10%, rgba(255, 255, 255, 0.15), transparent 45%)`,
    `radial-gradient(circle at 50% 120%, ${accentSecondary}, transparent 60%)`,
    baseGradient
  ].join(', ')
}

const hexToRgba = (hex: string, alpha = 1): string => {
  const sanitized = hex.replace('#', '')
  const bigint = parseInt(sanitized.length === 3 ? sanitized.repeat(2) : sanitized, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export default PortalHero
